import Receipt from "@/components/Receipt";
import { Lottie } from "lottie-react";
import cancelledAnimation from "../../lottie-animations/cancelled.json";
import successAnimation from "../../lottie-animations/success.json";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEffect } from "react";
import { isAndroid, isMobileOnly } from "react-device-detect";
import { QrcodeCanvas } from "react-qrcode-pretty";
import {
  FULL_WIDTH_TALL_BUTTON,
  GREEN_ACTION_BUTTON_DISABLED,
  GREEN_ACTION_BUTTON_ENABLED,
} from "@/lib/buttonStyles";
import { renderBankIconComponent } from "@/utils/bankInfo";
import type { Payment as PaymentType } from "@/model/payment";

interface Props {
  data: PaymentType | null | undefined;
  isPending: boolean;
  error: boolean;
}

const renderAlertText = (
  payment: PaymentType | null | undefined,
): { message: string; icon: React.ReactNode | undefined; error: boolean } => {
  switch (payment?.status) {
    case "REJECTED":
      return {
        message: "Greiðslu var hafnað",
        icon: (
          <Lottie
            src={cancelledAnimation}
            style={{
              width: isMobileOnly ? 128 : 160,
              height: isMobileOnly ? 128 : 160,
            }}
            autoplay
            loop={false}
          />
        ),
        error: true,
      };
    case "ERROR":
      return {
        message: "Villa kom upp við að framkvæma greiðslu",
        icon: (
          <Lottie
            src={cancelledAnimation}
            style={{
              width: isMobileOnly ? 128 : 160,
              height: isMobileOnly ? 128 : 160,
            }}
            autoplay
            loop={false}
          />
        ),
        error: true,
      };
    case "CANCELLED":
      return {
        message: "Hætt var við greiðslu",
        icon: (
          <Lottie
            src={cancelledAnimation}
            style={{
              width: isMobileOnly ? 128 : 160,
              height: isMobileOnly ? 128 : 160,
            }}
            autoplay
            loop={false}
          />
        ),
        error: true,
      };
    case "SUCCESS":
      return {
        message: "Greiðsla tókst!",
        icon: (
          <Lottie
            src={successAnimation}
            style={{
              width: isMobileOnly ? 160 : 200,
              height: isMobileOnly ? 160 : 200,
            }}
            autoplay
            loop={false}
          />
        ),
        error: false,
      };
    case "PENDING":
    case "SCA_REQUIRED":
      if (payment.paymentProvider === "isb") {
        return {
          message: "Bankinn þinn sendir þér tilkynningu í símann",
          icon: undefined,
          error: false,
        };
      }
      if (isMobileOnly) {
        return {
          message: "",
          icon: undefined,
          error: false,
        };
      } else {
        return {
          message:
            "Skannaðu QR kóðann\n\r(ath. að sumir bankar senda greiðslu beint í bankaapp)",
          icon: undefined,
          error: false,
        };
      }
    default:
      return { message: "", icon: undefined, error: false };
  }
};

export default function Payment({ data, isPending, error }: Props) {
  const urlPath = window.location.pathname;

  const handleOpenApp = () => {
    const redirectUrl = data?.scaRedirectUrl;
    if (redirectUrl) {
      window.location.assign(redirectUrl);
    }
  };

  // Android: Auto-redirect to SCA URL when available (reduces friction vs manual button tap)
  useEffect(() => {
    if (
      isAndroid &&
      data?.scaRedirectUrl &&
      data?.status === "SCA_REQUIRED" &&
      !urlPath.includes("sca-complete") &&
      data?.paymentProvider !== "isb"
    ) {
      window.location.assign(data.scaRedirectUrl);
    }
  }, [data?.scaRedirectUrl, data?.status, data?.paymentProvider, urlPath]);

  // Show receipt if payment is successful and there's no partnerRedirectUrl
  const shouldShowReceipt =
    data?.status === "SUCCESS" &&
    (!data?.partnerRedirectUrl || data.partnerRedirectUrl === "");

  if (shouldShowReceipt && data) {
    return <Receipt payment={data} />;
  }

  return (
    <div
      className={
        isMobileOnly
          ? "flex flex-col w-full items-center flex-1 self-stretch"
          : "flex flex-col w-full h-full"
      }
    >
      {/** Desktop mode - QR code section */}
      {!isMobileOnly && (
        <div className="flex flex-col items-center flex-1 min-h-0">
          {/* Desktop: Bank logo - always at top */}
          {data?.paymentProvider && (
            <div className="flex justify-center items-center w-full mt-8 mb-4 shrink-0">
              {renderBankIconComponent(String(data?.paymentProvider))}
            </div>
          )}
          {/* Desktop: Centered content area */}
          <div className="flex flex-col items-center justify-start flex-1 min-h-0 w-full gap-6 pt-12">
            {/* Desktop: Status text and animation */}
            {!error && !["DRAFT"].includes(String(data?.status)) && (
              <>
                {/* Desktop: Center animation/icon for error/success states */}
                {["REJECTED", "ERROR", "CANCELLED", "SUCCESS"].includes(
                  String(data?.status),
                ) && (
                  <div className="flex flex-col items-center gap-4">
                    {renderAlertText(data)?.icon}
                    <h2
                      className="text-2xl font-semibold text-foreground text-center"
                      style={{ fontFamily: '"DM Sans", sans-serif' }}
                      data-testid="payment-status-message"
                    >
                      {renderAlertText(data)?.message}
                    </h2>
                  </div>
                )}
                {["SCA_REQUIRED"].includes(String(data?.status)) &&
                  !urlPath.includes("sca-complete") && (
                    <div
                      className="flex flex-col items-center text-center whitespace-pre-wrap leading-7 text-md w-full px-4"
                      style={{ fontFamily: '"DM Sans", sans-serif' }}
                      data-testid="payment-status-message"
                    >
                      {renderAlertText(data)?.message}
                      <Spinner className="mt-8 size-6" />
                    </div>
                  )}
                {data &&
                  ["DRAFT", "PENDING", "SCA_REQUIRED"].includes(
                    data.status,
                  ) && (
                    <>
                      {urlPath.includes("sca-complete") && (
                        <Spinner className="size-6" />
                      )}
                    </>
                  )}
                {/** Show the QR code once we have the data..*/}
                {data?.scaRedirectUrl &&
                  data?.paymentProvider !== "isb" &&
                  ["SCA_REQUIRED"].includes(data.status) &&
                  !urlPath.includes("sca-complete") && (
                    <div
                      onClick={handleOpenApp}
                      className="cursor-pointer"
                      data-testid="payment-qr-code"
                    >
                      <QrcodeCanvas
                        value={data?.scaRedirectUrl}
                        variant={{
                          eyes: "fluid",
                          body: "standard",
                        }}
                        color={{
                          eyes: "#fff",
                          body: "#fff",
                        }}
                        padding={20}
                        level="L"
                        size={325}
                        bgColor="#4C2C64"
                        bgRounded
                        divider
                      />
                    </div>
                  )}
              </>
            )}
            {/** Loading state when we are waiting for the payload */}
            {isPending && !error && data?.paymentProvider !== "isb" && (
              <Spinner className="size-6" />
            )}
            {data && (
              <>
                {/** Got the payload but the payment status is still 'PENDING' */}
                {data.paymentProvider !== "isb" &&
                  ["PENDING"].includes(data.status) &&
                  !error && <Spinner className="size-6" />}
              </>
            )}
          </div>
        </div>
      )}
      {/** Mobile mode - Open bank app button section */}
      {isMobileOnly && (
        <>
          {/** Loading state when we are waiting for the payload */}
          {isPending && !error && data?.paymentProvider !== "isb" && (
            <Spinner className="size-6" />
          )}

          {/* Mobile: Bank logo - always at top */}
          {data?.paymentProvider && (
            <div className="flex justify-center items-center w-full mt-8 mb-4 shrink-0">
              {renderBankIconComponent(String(data?.paymentProvider))}
            </div>
          )}
          {!error && data && !["DRAFT"].includes(String(data?.status)) && (
            <div className="flex-1 w-full flex flex-col items-center justify-start self-stretch gap-6 pt-8">
              {["REJECTED", "ERROR", "CANCELLED", "SUCCESS"].includes(
                String(data?.status),
              ) && (
                <div className="flex flex-col items-center gap-4 px-4">
                  {renderAlertText(data)?.icon}
                  <h2
                    className="text-2xl font-semibold text-foreground text-center"
                    style={{ fontFamily: '"DM Sans", sans-serif' }}
                    data-testid="payment-status-message"
                  >
                    {renderAlertText(data)?.message}
                  </h2>
                </div>
              )}
              {["SCA_REQUIRED"].includes(String(data?.status)) &&
                !urlPath.includes("sca-complete") && (
                  <div
                    className="flex flex-col items-center text-center whitespace-pre-wrap leading-7 text-md w-full px-4"
                    style={{ fontFamily: '"DM Sans", sans-serif' }}
                    data-testid="payment-status-message"
                  >
                    {renderAlertText(data)?.message}
                    <Spinner className="mt-8" />
                  </div>
                )}
              {![
                "SCA_REQUIRED",
                "REJECTED",
                "ERROR",
                "CANCELLED",
                "SUCCESS",
              ].includes(String(data?.status)) && (
                <div
                  className="flex flex-col items-center text-center whitespace-pre-wrap leading-7 text-md w-full px-4"
                  style={{ fontFamily: '"DM Sans", sans-serif' }}
                  data-testid="payment-status-message"
                >
                  <Spinner className="mt-8" />
                </div>
              )}
            </div>
          )}
          {data &&
            data.paymentProvider !== "isb" &&
            ["DRAFT", "PENDING", "SCA_REQUIRED"].includes(data.status) && (
              <>
                {data.status === "SCA_REQUIRED" &&
                  !urlPath.includes("sca-complete") && (
                    <div className="w-full pb-8 mt-auto">
                      <Button
                        className={
                          data.scaRedirectUrl
                            ? `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_ENABLED}`
                            : `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_DISABLED}`
                        }
                        onClick={handleOpenApp}
                        disabled={!data.scaRedirectUrl}
                      >
                        Opna banka app
                      </Button>
                    </div>
                  )}
              </>
            )}
        </>
      )}
    </div>
  );
}
